/**
 * AhaMomentBanners — empty states + completion nudges for the Simple product view.
 *
 * Part of Plan 1897 (Aha Moment Simple View), Features 5 & 6.
 * Calm, iPad-Settings aesthetic — encouragement, never pressure.
 *
 * - MotivationalEmptyState : shown when the store has 0 products (first-time UX)
 * - FilteredEmptyState     : shown when a filter/search hides all products
 * - AlmostReadyNudge        : shown when products exist but none are on the storefront
 * - StoreReadyIndicator     : subtle "your store is live" milestone marker
 */

import { PackagePlus, Sparkles, Store, CheckCircle2, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";

type Lang = string;

const isTh = (l: Lang) => l === "th";

/** 0 products — warm first-time empty state with a single clear CTA. */
export function MotivationalEmptyState({
  language,
  onAddProduct,
}: {
  language: Lang;
  onAddProduct: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-20">
      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-50 dark:bg-sky-900/20 text-sky-500 dark:text-sky-400 mb-5 shadow-apple-sm">
        <Store className="w-8 h-8" strokeWidth={1.75} />
      </div>
      <h3 className="text-[17px] font-semibold text-neutral-900 dark:text-neutral-100">
        {isTh(language) ? "สินค้าของคุณจะปรากฏที่นี่" : "Your products will appear here"}
      </h3>
      <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-neutral-500 dark:text-neutral-400">
        {isTh(language)
          ? "เพิ่มสินค้าชิ้นแรกเพื่อเริ่มต้นร้านของคุณ แล้วเปิดขายบนหน้าร้านได้ทันที"
          : "Add your first product to bring your store to life — then publish it to your storefront in one tap."}
      </p>
      <Button onClick={onAddProduct} className="mt-6 gap-2">
        <PackagePlus className="w-4 h-4" />
        {isTh(language) ? "เพิ่มสินค้าชิ้นแรก" : "Add your first product"}
      </Button>
    </div>
  );
}

/** Filter/search returned nothing — compact, with a clear-filter escape hatch. */
export function FilteredEmptyState({
  language,
  onClear,
}: {
  language: Lang;
  onClear?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-16">
      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 mb-4">
        <ImageOff className="w-6 h-6" strokeWidth={1.75} />
      </div>
      <p className="text-[14px] font-medium text-neutral-700 dark:text-neutral-300">
        {isTh(language) ? "ไม่พบสินค้าที่ตรงกับตัวกรอง" : "No products match this filter"}
      </p>
      {onClear && (
        <Button variant="outline" size="sm" onClick={onClear} className="mt-4">
          {isTh(language) ? "ล้างตัวกรอง" : "Clear filter"}
        </Button>
      )}
    </div>
  );
}

/** All products are Draft — a gentle nudge to publish the first one. */
export function AlmostReadyNudge({ language }: { language: Lang }) {
  return (
    <div className="mx-3 mb-2 flex items-start gap-3 rounded-xl border border-amber-200/70 bg-amber-50/70 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-900/15">
      <Sparkles className="mt-0.5 w-4 h-4 shrink-0 text-amber-500 dark:text-amber-400" />
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-amber-800 dark:text-amber-300">
          {isTh(language) ? "ร้านของคุณเกือบพร้อมแล้ว" : "Your store is almost ready"}
        </p>
        <p className="mt-0.5 text-[12px] leading-relaxed text-amber-700/80 dark:text-amber-400/80">
          {isTh(language)
            ? "เปิดการแสดงสินค้าสักชิ้นบนหน้าร้าน เพื่อให้ลูกค้าเห็นและซื้อได้จริง"
            : "Enable a product on your storefront to see it live for your customers."}
        </p>
      </div>
    </div>
  );
}

/** Store has at least one fully-ready live product — a calm milestone marker. */
export function StoreReadyIndicator({ language }: { language: Lang }) {
  return (
    <div className="mx-3 mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50/70 px-3 py-1.5 dark:border-emerald-900/40 dark:bg-emerald-900/15">
      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
      <span className="text-[12px] font-medium text-emerald-700 dark:text-emerald-400">
        {isTh(language) ? "ร้านของคุณพร้อมขายแล้ว" : "Your store is live"}
      </span>
    </div>
  );
}
